import { useEffect, useState } from 'react';
import axios from 'axios';
import { EnvelopeSimple, FloppyDisk, PaperPlaneTilt, ShieldCheck, Clock } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { formatDateTime } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [password, setPassword] = useState('');
  const [containment, setContainment] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email-settings`);
      setSettings(res.data);
      // Prefer the last test address; fall back to mail_from
      setTestTo(res.data.last_test_to || res.data.mail_from || '');
    } catch (e) {
      toast.error('メール設定の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    axios.get(`${API}/email-settings/containment`)
      .then((r) => setContainment(r.data)).catch(() => setContainment(null));
  }, []);

  const handleField = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...settings };
      delete payload.mail_password_set;
      delete payload.updated_at;
      delete payload.id;
      delete payload.template_folder;
      if (password) payload.mail_password = password;
      else delete payload.mail_password;
      const res = await axios.put(`${API}/email-settings`, payload);
      setSettings(res.data);
      setPassword('');
      toast.success('メール設定を保存しました');
    } catch (e) {
      toast.error(e.response?.data?.detail || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim()) {
      toast.error('テスト送信先メールアドレスを入力してください');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/email-settings/test`, { to: testTo.trim() });
      toast.success(`テストメールを ${testTo} へ送信しました`);
      // Refresh to pick up newly-saved last_test_at
      setSettings((prev) => ({
        ...prev,
        last_test_to: testTo.trim(),
        last_test_at: new Date().toISOString(),
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'テストメールの送信に失敗しました');
    } finally {
      setSending(false);
    }
  };

  if (loading || !settings) {
    return <p className="text-zinc-500 text-center py-12">読み込み中...</p>;
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="email-settings-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2"
          data-testid="settings-page-title">
          <EnvelopeSimple size={28} weight="duotone" className="text-teal-700" />
          メール設定
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          通知メールの自動送信スケジュールと、SMTP サーバーの設定を管理します。
        </p>
      </div>

      {/* 封じ込めの状態。バナーの「詳細」の着地点。止まっていないときは何も出さない。 */}
      {containment?.enabled && (
        <div className="rounded-lg border-2 border-rose-300 bg-rose-50 p-4" data-testid="containment-detail">
          <p className="text-sm font-bold text-rose-800">
            {containment.mode === 'redirect'
              ? `メールは転送されています（宛先: ${(containment.sink || []).join('、')}）`
              : 'メールは送信されていません（SMTP へ渡していません）'}
          </p>
          <dl className="mt-2 space-y-1 text-xs text-rose-900">
            <div><dt className="inline font-semibold">理由: </dt><dd className="inline">{containment.reason}</dd></div>
            <div><dt className="inline font-semibold">判定に使った変数: </dt><dd className="inline">{(containment.keys || []).join(', ') || '—'}</dd></div>
            <div><dt className="inline font-semibold">直近24時間: </dt><dd className="inline">{containment.recent_24h ?? 0} 件</dd></div>
            {containment.last && (
              <div><dt className="inline font-semibold">最後の件名: </dt>
                <dd className="inline">{containment.last.subject}（{(containment.last.intended_recipients || []).join('、')}）</dd></div>
            )}
          </dl>
          <p className="mt-2 text-[11px] text-rose-700">
            本番でこれが出ている場合は設定の誤りです。Secrets の FRONTEND_URL / BACKUP_API_URL を
            本番の URL に直してください（preview の URL が1つでも残っていると判定されます）。
          </p>
        </div>
      )}

      {/* Auto-send schedule card — surfaced at the top so it's the first thing
          admins see when they come to fine-tune the leave-email cadence. */}
      <div className="bg-white rounded-lg border border-zinc-200 p-6 space-y-5" data-testid="auto-send-time-card">
        <div>
          <h3 className="font-bold text-zinc-900 flex items-center gap-2 text-base">
            <Clock size={20} weight="duotone" className="text-amber-500" />
            自動送信スケジュール (休暇届)
          </h3>
          <p className="text-sm text-zinc-500 mt-1">
            休暇届の不在連絡メールを自動送信するタイミングを設定します。タイムゾーンは <span className="font-mono text-zinc-700">Asia/Tokyo</span> (JST) です。
          </p>
        </div>

        {/* Pre-notification (N working days before) */}
        <div className="rounded-lg border border-zinc-200 bg-zinc-50/40 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-semibold border border-blue-200">事前</span>
            <span className="text-sm font-semibold text-zinc-900">事前通知</span>
            <span className="text-[11px] text-zinc-500">休暇開始日より前に送信されるプレビュー メール</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="font-bold text-xs text-zinc-700">送信日 (営業日前)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={10}
                  step={1}
                  data-testid="input-leave-pre-send-days"
                  value={settings.leave_pre_send_days_before ?? ''}
                  onChange={(e) => handleField('leave_pre_send_days_before', e.target.value)}
                  placeholder="2"
                  className="w-24"
                />
                <span className="text-sm text-zinc-600 whitespace-nowrap">営業日前に送信</span>
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                未設定の場合は <span className="font-mono text-zinc-700">2</span> 営業日前 (1〜10)。
              </p>
            </div>
            <div>
              <Label className="font-bold text-xs text-zinc-700">送信時刻</Label>
              <Input
                type="time"
                data-testid="input-leave-pre-send-time"
                value={settings.leave_pre_send_time || ''}
                onChange={(e) => handleField('leave_pre_send_time', e.target.value)}
                placeholder="08:00"
                step="60"
              />
              <p className="text-xs text-zinc-500 mt-1">
                未設定の場合は <span className="font-mono text-zinc-700">08:00</span>。
              </p>
            </div>
          </div>
        </div>

        {/* On-start day */}
        <div className="rounded-lg border border-zinc-200 bg-zinc-50/40 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold border border-emerald-200">当日</span>
            <span className="text-sm font-semibold text-zinc-900">当日通知</span>
            <span className="text-[11px] text-zinc-500">休暇開始日の朝に送信される不在連絡メール</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="font-bold text-xs text-zinc-700">送信日</Label>
              <Input
                value="休暇開始日 (固定)"
                disabled
                className="bg-zinc-100 text-zinc-500"
              />
              <p className="text-xs text-zinc-500 mt-1">この通知は必ず開始日に送信されます。</p>
            </div>
            <div>
              <Label className="font-bold text-xs text-zinc-700">送信時刻</Label>
              <Input
                type="time"
                data-testid="input-leave-on-send-time"
                value={settings.leave_on_send_time || ''}
                onChange={(e) => handleField('leave_on_send_time', e.target.value)}
                placeholder="08:00"
                step="60"
              />
              <p className="text-xs text-zinc-500 mt-1">
                未設定の場合は <span className="font-mono text-zinc-700">08:00</span>。
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-900 leading-relaxed">
          ※ 既に予約済みの送信時刻は変更されません。新しい設定は、保存後に登録される休暇届から適用されます。
          既存予約の再スケジュールは、対象の申請を再保存することでも反映できます。
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="btn-save-auto-time" className="bg-teal-700 hover:bg-teal-800">
            <FloppyDisk size={18} className="mr-2" />
            {saving ? '保存中...' : '保存'}
          </Button>
        </div>
      </div>

      {/* SMTP server card */}
      <div>
        <h3 className="font-bold text-zinc-900 flex items-center gap-2 text-base mb-2">
          <EnvelopeSimple size={20} weight="duotone" className="text-teal-700" />
          メール (SMTP) サーバー
        </h3>
      </div>

      <div className="bg-white rounded-lg border border-zinc-200 p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="font-bold">SMTPサーバー *</Label>
            <Input
              data-testid="input-mail-server"
              value={settings.mail_server || ''}
              onChange={(e) => handleField('mail_server', e.target.value)}
              placeholder="mail.sensbey.co.jp"
            />
          </div>
          <div>
            <Label className="font-bold">ポート *</Label>
            <Input
              type="number"
              data-testid="input-mail-port"
              value={settings.mail_port || 587}
              onChange={(e) => handleField('mail_port', e.target.value)}
              placeholder="587"
            />
          </div>
          <div>
            <Label className="font-bold">ユーザー名 (送信用アカウント) *</Label>
            <Input
              data-testid="input-mail-username"
              value={settings.mail_username || ''}
              onChange={(e) => handleField('mail_username', e.target.value)}
              placeholder="user@sensbey.co.jp"
            />
          </div>
          <div>
            <Label className="font-bold">パスワード</Label>
            <Input
              type="password"
              data-testid="input-mail-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={settings.mail_password_set ? '••••• (変更時のみ入力)' : '未設定'}
            />
            <p className="text-xs text-zinc-500 mt-1">
              {settings.mail_password_set ? '保存済み。変更しない場合は空欄のまま。' : '初回設定です。'}
            </p>
          </div>
          <div>
            <Label className="font-bold">差出人メールアドレス</Label>
            <Input
              data-testid="input-mail-from"
              value={settings.mail_from || ''}
              onChange={(e) => handleField('mail_from', e.target.value)}
              placeholder="user@sensbey.co.jp"
            />
          </div>
          <div>
            <Label className="font-bold">差出人名</Label>
            <Input
              data-testid="input-mail-from-name"
              value={settings.mail_from_name || ''}
              onChange={(e) => handleField('mail_from_name', e.target.value)}
              placeholder="Sensbey 勤怠管理"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2 border-t border-zinc-200">
          <ToggleRow
            label="STARTTLS"
            description="標準的な暗号化 (ポート 587)"
            testId="toggle-starttls"
            checked={settings.mail_starttls}
            onChange={(v) => handleField('mail_starttls', v)}
          />
          <ToggleRow
            label="SSL/TLS"
            description="ポート 465 などの暗号化"
            testId="toggle-ssl"
            checked={settings.mail_ssl_tls}
            onChange={(v) => handleField('mail_ssl_tls', v)}
          />
          <ToggleRow
            label="認証を使用"
            description="ユーザー名・パスワードで認証"
            testId="toggle-creds"
            checked={settings.use_credentials}
            onChange={(v) => handleField('use_credentials', v)}
          />
          <ToggleRow
            label="証明書を検証"
            description="自己署名/ホスト名不一致のサーバーではOFFにしてください"
            testId="toggle-validate"
            checked={settings.validate_certs}
            onChange={(v) => handleField('validate_certs', v)}
          />
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={saving} data-testid="btn-save-email-settings" className="bg-teal-700 hover:bg-teal-800">
            <FloppyDisk size={18} className="mr-2" />
            {saving ? '保存中...' : '保存'}
          </Button>
        </div>
      </div>

      {/* Test mail */}
      <div className="bg-white rounded-lg border border-zinc-200 p-6 space-y-4">
        <h3 className="font-bold text-zinc-900 flex items-center gap-2">
          <ShieldCheck size={20} weight="duotone" className="text-emerald-600" />
          テストメール送信
        </h3>
        <p className="text-sm text-zinc-500">
          現在保存されている設定でテストメールを送信し、SMTP 疎通を確認します。
        </p>
        {settings.last_test_at && (
          <p className="text-xs text-zinc-500" data-testid="last-test-info">
            最終テスト送信: <span className="font-medium text-zinc-700">{settings.last_test_to}</span>{' '}
            <span className="text-zinc-400">
              ({formatDateTime(settings.last_test_at)})
            </span>
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="test@example.com"
            data-testid="input-test-to"
            className="flex-1"
          />
          <Button onClick={sendTest} disabled={sending} data-testid="btn-send-test" className="bg-teal-700 hover:bg-teal-800">
            <PaperPlaneTilt size={18} className="mr-2" />
            {sending ? '送信中...' : 'テスト送信'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ label, description, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-md border border-zinc-100">
      <div>
        <p className="font-medium text-sm text-zinc-900">{label}</p>
        <p className="text-xs text-zinc-500">{description}</p>
      </div>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}
